/**
 * FormLoadingOverlay Component
 * Shows a loading overlay during form submission to prevent interaction
 */
import React from 'react';
import { LoadingSpinner } from './LoadingSpinner';

export interface FormLoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  className?: string;
}

export const FormLoadingOverlay: React.FC<FormLoadingOverlayProps> = ({
  isLoading,
  message = 'Submitting...',
  className = '',
}) => {
  if (!isLoading) return null;

  return (
    <div
      className={`fixed inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center ${className}`}
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 flex flex-col items-center space-y-4 min-w-[200px]">
        <LoadingSpinner size="lg" />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{message}</p>
      </div>
    </div>
  );
};

/**
 * FormLoadingOverlayInline Component
 * Inline version for use within form containers (not full screen)
 */
export interface FormLoadingOverlayInlineProps {
  isLoading: boolean;
  message?: string;
  className?: string;
}

export const FormLoadingOverlayInline: React.FC<FormLoadingOverlayInlineProps> = ({
  isLoading,
  message = 'Submitting...',
  className = '',
}) => {
  if (!isLoading) return null;

  return (
    <div
      className={`absolute inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl ${className}`}
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-4 flex flex-col items-center space-y-3">
        <LoadingSpinner size="md" />
        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{message}</p>
      </div>
    </div>
  );
};
